import { useQuery } from "@tanstack/react-query";
import {
  ArrowUpRight,
  ArrowDownRight,
  Filter,
  Download,
  Calendar,
} from "lucide-react";
import { MainLayout } from "@/components/layout/main-layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Trade, Order } from "@shared/schema";

function formatCurrency(value: number | string | null | undefined): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (num === null || num === undefined || isNaN(num)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

function formatPercent(value: number | string | null | undefined): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (num === null || num === undefined || isNaN(num)) return "0.00%";
  return `${num >= 0 ? "+" : ""}${num.toFixed(2)}%`;
}

function formatDate(date: string | Date | null | undefined): string {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function TradeRow({ trade }: { trade: Trade }) {
  const profitLoss = parseFloat(trade.profitLoss?.toString() || "0");
  const isPositive = profitLoss >= 0;

  return (
    <TableRow className="hover-elevate">
      <TableCell>
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center">
            <span className="text-xs font-bold text-primary">
              {trade.symbol?.slice(0, 2).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="font-medium">{trade.symbol}</p>
            <p className="text-xs text-muted-foreground">{trade.securityType || "Stock"}</p>
          </div>
        </div>
      </TableCell>
      <TableCell className="font-mono">{trade.quantity}</TableCell>
      <TableCell className="font-mono">{formatCurrency(trade.buyPrice)}</TableCell>
      <TableCell className="font-mono">{formatCurrency(trade.sellPrice)}</TableCell>
      <TableCell className="text-sm text-muted-foreground">{formatDate(trade.buyDate)}</TableCell>
      <TableCell className="text-sm text-muted-foreground">{formatDate(trade.sellDate)}</TableCell>
      <TableCell>
        <div className={`flex items-center gap-1 ${isPositive ? "text-chart-1" : "text-destructive"}`}>
          {isPositive ? (
            <ArrowUpRight className="h-4 w-4" />
          ) : (
            <ArrowDownRight className="h-4 w-4" />
          )}
          <span className="font-mono">{formatCurrency(profitLoss)}</span>
        </div>
      </TableCell>
      <TableCell className={`font-mono ${isPositive ? "text-chart-1" : "text-destructive"}`}>
        {formatPercent(trade.profitLossPercent)}
      </TableCell>
    </TableRow>
  );
}

function OrderRow({ order }: { order: Order }) {
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "filled":
        return "bg-chart-1/20 text-chart-1";
      case "pending":
        return "bg-chart-4/20 text-chart-4";
      case "cancelled":
        return "bg-muted text-muted-foreground";
      case "rejected":
        return "bg-destructive/20 text-destructive";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getSideColor = (side: string) => {
    return side.toLowerCase().includes("buy") ? "text-chart-1" : "text-destructive";
  };

  return (
    <TableRow className="hover-elevate">
      <TableCell>
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center">
            <span className="text-xs font-bold text-primary">
              {order.symbol?.slice(0, 2).toUpperCase()}
            </span>
          </div>
          <p className="font-medium">{order.symbol}</p>
        </div>
      </TableCell>
      <TableCell>
        <span className={`font-medium ${getSideColor(order.side)}`}>{order.side}</span>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="text-xs">{order.orderType}</Badge>
      </TableCell>
      <TableCell className="font-mono">{order.quantity}</TableCell>
      <TableCell className="font-mono">
        {order.orderType === "Market" ? "Market" : formatCurrency(order.limitPrice)}
      </TableCell>
      <TableCell className="font-mono">{formatCurrency(order.averagePrice)}</TableCell>
      <TableCell>
        <Badge className={getStatusColor(order.status)}>{order.status}</Badge>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{formatDate(order.createdAt)}</TableCell>
    </TableRow>
  );
}

export default function HistoryPage() {
  const { data: trades, isLoading: tradesLoading } = useQuery<Trade[]>({
    queryKey: ["/api/trades"],
  });

  const { data: orders, isLoading: ordersLoading } = useQuery<Order[]>({
    queryKey: ["/api/orders"],
  });

  const totalPL = trades?.reduce(
    (sum, trade) => sum + parseFloat(trade.profitLoss?.toString() || "0"),
    0
  ) || 0;

  const winningTrades = trades?.filter(
    (trade) => parseFloat(trade.profitLoss?.toString() || "0") > 0
  ).length || 0;

  const winRate = trades && trades.length > 0 ? (winningTrades / trades.length) * 100 : 0;

  return (
    <MainLayout
      title="Trade History"
      subtitle="View your past trades and orders"
      actions={
        <div className="flex gap-2">
          <Button variant="outline" size="sm" data-testid="button-filter">
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
          <Button variant="outline" size="sm" data-testid="button-date-range">
            <Calendar className="h-4 w-4 mr-2" />
            Date Range
          </Button>
          <Button variant="outline" size="sm" data-testid="button-export">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total P&L</p>
              {tradesLoading ? (
                <Skeleton className="h-7 w-24 mt-1" />
              ) : (
                <p className={`text-2xl font-bold ${totalPL >= 0 ? "text-chart-1" : "text-destructive"}`}>
                  {totalPL >= 0 ? "+" : ""}{formatCurrency(totalPL)}
                </p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total Trades</p>
              {tradesLoading ? (
                <Skeleton className="h-7 w-12 mt-1" />
              ) : (
                <p className="text-2xl font-bold">{trades?.length || 0}</p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Win Rate</p>
              {tradesLoading ? (
                <Skeleton className="h-7 w-16 mt-1" />
              ) : (
                <p className="text-2xl font-bold">{winRate.toFixed(1)}%</p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total Orders</p>
              {ordersLoading ? (
                <Skeleton className="h-7 w-12 mt-1" />
              ) : (
                <p className="text-2xl font-bold">{orders?.length || 0}</p>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-medium">Closed Trades</CardTitle>
          </CardHeader>
          <CardContent>
            {tradesLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : trades && trades.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Symbol</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>Buy Price</TableHead>
                      <TableHead>Sell Price</TableHead>
                      <TableHead>Buy Date</TableHead>
                      <TableHead>Sell Date</TableHead>
                      <TableHead>P&L</TableHead>
                      <TableHead>Return</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trades.map((trade) => (
                      <TradeRow key={trade.id} trade={trade} />
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                No closed trades yet
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-medium">Order History</CardTitle>
          </CardHeader>
          <CardContent>
            {ordersLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : orders && orders.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Symbol</TableHead>
                      <TableHead>Side</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Avg Fill</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order) => (
                      <OrderRow key={order.id} order={order} />
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                No orders yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
